import { LocalizedText, showErrorAlert, showSuccessAlert } from '@mews/framework';
import { TextKey } from '@mews-commander/enums';
import { Button, Dialog } from '@optimus-web/core';
import { useRepositoryMutation, DetailedPlatformProfileResult } from '@mews-commander/data-access';

interface ResetAuthenticatorPlatformProfileProps {
    platformProfile: DetailedPlatformProfileResult;
    onClose: () => void;
}

export const ResetAuthenticatorPlatformProfileDialog: React.FC<ResetAuthenticatorPlatformProfileProps> = ({
    platformProfile,
    onClose,
}) => {
    const { resetAuthenticatorPlatformProfile, isPending } = useResetAuthenticatorPlatformProfileProfiles(
        platformProfile,
        onClose
    );

    return (
        <Dialog
            title={
                <LocalizedText
                    textKey={TextKey.TwoFactorAuthenticationReset}
                    substitutes={{ user: platformProfile.fullName || '' }}
                />
            }
            loading={isPending}
            onClose={onClose}
            footer={{
                secondary: (
                    <Button onClick={onClose} variant="tertiary" size="medium" data-test-id="cancel" type="submit">
                        <LocalizedText textKey={TextKey.Cancel} />
                    </Button>
                ),
                primary: (
                    <Button
                        onClick={resetAuthenticatorPlatformProfile}
                        variant="primary"
                        size="medium"
                        data-test-id="reset"
                        type="submit"
                    >
                        <LocalizedText textKey={TextKey.ResetTwoFactorShort} />
                    </Button>
                ),
            }}
            active
        >
            <LocalizedText textKey={TextKey.TwoFactorAuthenticationResetDescription} />
        </Dialog>
    );
};

export const useResetAuthenticatorPlatformProfileProfiles = (
    platformProfile: Pick<DetailedPlatformProfileResult, 'fullName' | 'profileId'>,
    onClose: () => void
) => {
    const mutation = useRepositoryMutation('platformProfiles', 'resetAuthenticator', {
        onSuccess: () => {
            showSuccessAlert({
                description: (
                    <LocalizedText
                        textKey={TextKey.TwoFactorAuthenticationResetSuccess}
                        substitutes={{ name: platformProfile.fullName || '' }}
                    />
                ),
            });
        },
        invalidateQueries: {
            platformProfiles: ['getAll', 'get'],
        },
        meta: {
            disableDefaultErrorHandler: true,
        },
        onError: (error: Error) => {
            showErrorAlert({
                description: error.message ? (
                    <>{error.message}</>
                ) : (
                    <LocalizedText
                        textKey={TextKey.AuthenticatorDisableWarningMessage}
                        substitutes={{ name: platformProfile.fullName || '' }}
                    />
                ),
            });
        },
    });

    const resetAuthenticatorPlatformProfile = () => {
        mutation.mutate(
            { platformProfileId: platformProfile.profileId },
            {
                onSettled: () => {
                    onClose();
                },
            }
        );
    };

    return {
        resetAuthenticatorPlatformProfile,
        isPending: mutation.isPending,
    };
};
